'use client'

import { useState } from 'react'
import { Menu } from 'lucide-react'
import { useThemeStore } from '@/lib/stores/theme-store'
import { WhiteLabelAssets, BrandedContent } from '../white-label-assets'
import { Skeleton } from '@/components/ui/skeleton'
import { MobileNavDrawer } from './mobile-nav-drawer'

const isTrueFlag = (value: unknown): boolean => value === true || value === 'true'

export function LoginHeader() {
  const { currentTheme } = useThemeStore()
  const brandName = currentTheme?.title || 'Mentorfy'
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const hasHeaderFields = currentTheme?.custom_header_fields && currentTheme.custom_header_fields.length > 0
  const visibleFields = currentTheme?.custom_header_fields?.filter((field) => !isTrueFlag(field.only_members)) || []

  // Se tem 2 ou menos campos, mostra inline no mobile. Se 3+, usa drawer
  const shouldUseDrawer = visibleFields.length > 2

  return (
    <>
      <header className="absolute top-4 sm:top-6 left-4 sm:left-8 right-4 sm:right-8 z-30">
        <div className="flex items-center justify-between gap-4">
          {/* Logo e campos inline (quando até 2 campos) */}
          <div className="flex items-center gap-4 sm:gap-6 lg:gap-8">
            <BrandedContent fallback={<Skeleton className="h-6 sm:h-7 w-24 sm:w-32" />}>
              <WhiteLabelAssets
                type="logo-light"
                className="h-6 sm:h-7 w-auto"
                alt={`${brandName} Logo`}
              />
            </BrandedContent>

            {/* Se tem até 2 campos: mostra sempre inline à esquerda */}
            {hasHeaderFields && !shouldUseDrawer && (
              <nav className="flex items-center gap-3 sm:gap-4 lg:gap-6">
                {visibleFields.map((field, index) => (
                  <a
                    key={`${field.id}-${index}`}
                    href={field.url}
                    target={field.url.startsWith('http') ? '_blank' : undefined}
                    rel={field.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-xs sm:text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap"
                    style={{ color: 'var(--login-header-text, #ffffff)' }}
                  >
                    {field.label}
                  </a>
                ))}
              </nav>
            )}
          </div>

          {/* Se tem 3+ campos: inline no desktop à direita, drawer no mobile */}
          {hasHeaderFields && shouldUseDrawer && (
            <div className="flex items-center gap-3 sm:gap-4 lg:gap-6">
              <nav className="hidden md:flex items-center gap-4 lg:gap-6">
                {visibleFields.map((field, index) => (
                  <a
                    key={`${field.id}-${index}`}
                    href={field.url}
                    target={field.url.startsWith('http') ? '_blank' : undefined}
                    rel={field.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-sm font-medium transition-colors hover:opacity-70 whitespace-nowrap"
                    style={{ color: 'var(--login-header-text, #ffffff)' }}
                  >
                    {field.label}
                  </a>
                ))}
              </nav>

              {/* Botão Menu Mobile - apenas para 3+ campos */}
              <button
                onClick={() => setIsDrawerOpen(true)}
                className="md:hidden p-2 rounded-lg transition-all hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
                aria-label="Abrir menu"
                aria-expanded={isDrawerOpen}
              >
                <Menu className="w-5 h-5" style={{ color: 'var(--login-header-text, #ffffff)' }} />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Navigation Drawer - apenas para 3+ campos */}
      {hasHeaderFields && shouldUseDrawer && (
        <MobileNavDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          headerFields={visibleFields}
        />
      )}
    </>
  )
}

